import { useCallback } from 'react'
import { uniq } from 'lodash-es'
import type { ShearAnchorPosition } from '@api/fastAPI.schemas'
import { resolveSelectedElementType } from '@editor/utils'
import {
  ResultsViewMode,
  ResultsViewTab,
  ResultsViewWallSubTab,
  useEditorViewStore,
  useModelStore,
  useSelectionStore,
} from '@editorStores'

const emptyElements: string[] = []
const emptyShearAnchorLocations: ShearAnchorLocationSelection[] = []

const resultsViewModes: ResultsViewMode[] = ['comments', 'tensile-connectors-table', 'blockpad']

/**
 * Results selection backed by the global selection store, exposing the action/state names that
 * used to live on the editor view store's results slice. Selection (elements, stiffening segment,
 * load, connector, transmitter, check position, mode) lives in the selection store; the tab/pane
 * state around it stays in the editor view store, and combined actions update both.
 */
const useResultsSelection = () => {
  // Scoped selectors (one narrowed value each) so unrelated selection-store changes don't
  // re-render every consumer of this hook.
  const selectedElements = useSelectionStore(state =>
    state.selection?.type === 'planar' ? state.selection.guids : emptyElements,
  )
  const selectedStiffeningSegment = useSelectionStore(state =>
    state.selection?.type === 'stiffening' ? state.selection.guids[0] ?? null : null,
  )
  // The single anchor whose detail pane is open; null while several are selected (the "Anker"
  // tab then shows its multi-edit pane instead).
  const selectedAnchor = useSelectionStore(state =>
    state.selection?.type === 'anchor' && state.selection.guids.length === 1
      ? state.selection.guids[0]
      : null,
  )
  const selectedAnchors = useSelectionStore(state =>
    state.selection?.type === 'anchor' ? state.selection.guids : emptyElements,
  )
  // The single Schubanker whose detail pane is open (segment guid and position); null while
  // several are selected (the "Schubanker" tab then shows its multi-edit pane instead).
  const selectedShearAnchor = useSelectionStore(state =>
    state.selection?.type === 'shear-anchor' && state.selection.locations.length === 1
      ? state.selection.locations[0].guid
      : null,
  )
  const selectedShearAnchorPosition = useSelectionStore(state =>
    state.selection?.type === 'shear-anchor' && state.selection.locations.length === 1
      ? state.selection.locations[0].position
      : null,
  )
  const selectedShearAnchorLocations = useSelectionStore(state =>
    state.selection?.type === 'shear-anchor'
      ? state.selection.locations
      : emptyShearAnchorLocations,
  )
  const selectedLoad = useSelectionStore(state =>
    state.subSelection?.type === 'load' ? state.subSelection.guid : null,
  )
  const selectedConnector = useSelectionStore(state =>
    state.subSelection?.type === 'connector' ? state.subSelection.guid : null,
  )
  const selectedVerticalTransmitter = useSelectionStore(state =>
    state.subSelection?.type === 'vertical-transmitter' ? state.subSelection.guid : null,
  )
  const selectedCheckPosition = useSelectionStore(state =>
    state.subSelection?.type === 'member-position' ? state.subSelection.guid : null,
  )
  const activeMode = useSelectionStore(state =>
    resultsViewModes.includes(state.mode as ResultsViewMode)
      ? (state.mode as ResultsViewMode)
      : null,
  )
  const select = useSelectionStore(state => state.select)
  const selectSub = useSelectionStore(state => state.selectSub)
  const switchMode = useSelectionStore(state => state.switchMode)
  const clearSelection = useSelectionStore(state => state.clearSelection)

  /** Planar selection with its element type resolved (single selections only — a Results
   *  multi-selection stays untyped like any mixed selection). The model is read imperatively at
   *  click time: subscribing to it here would re-render every consumer of this hook — including
   *  small leaf controls and scene components — on each model edit, just for this callback. */
  const toResultsPlanarSelection = useCallback(
    (guids: string[]): PlanarElementSelection | null =>
      guids.length
        ? {
            type: 'planar',
            elementType:
              guids.length === 1
                ? resolveSelectedElementType(useModelStore.getState().model, guids[0])
                : undefined,
            guids,
          }
        : null,
    [],
  )

  const setResultsTabStore = useEditorViewStore(state => state.setResultsTab)
  const setResultsWallSubTabStore = useEditorViewStore(state => state.setResultsWallSubTab)
  const setResultsDetailTab = useEditorViewStore(state => state.setResultsDetailTab)
  const setResultsTracedLoads = useEditorViewStore(state => state.setResultsTracedLoads)
  const setResultsLocalTab = useEditorViewStore(state => state.setResultsLocalTab)
  const setResultsIsMultiEditMode = useEditorViewStore(state => state.setResultsIsMultiEditMode)

  const selectResultsPosition = useCallback(
    (position?: string, tab?: ResultsViewTab, wallSubTab?: ResultsViewWallSubTab) => {
      if (tab) setResultsTabStore(tab, wallSubTab)
      else {
        if (wallSubTab) setResultsWallSubTabStore(wallSubTab)
        setResultsDetailTab('design')
        setResultsTracedLoads(null)
        setResultsIsMultiEditMode(false)
      }
      select(toResultsPlanarSelection(position ? [position] : []), { adopt: 'keep' })
    },
    [
      setResultsTabStore,
      setResultsWallSubTabStore,
      setResultsDetailTab,
      setResultsTracedLoads,
      setResultsIsMultiEditMode,
      select,
      toResultsPlanarSelection,
    ],
  )

  /** Select a slab-beam and open its merged Dach/Decke surface tab (e.g. when clicking a slab). */
  const selectResultsSurface = useCallback(
    (surfaceGuid: string, tab: ResultsViewTab) => {
      setResultsTabStore(tab)
      select(toResultsPlanarSelection([surfaceGuid]), { adopt: 'keep' })
    },
    [setResultsTabStore, select, toResultsPlanarSelection],
  )

  const selectResultsStiffeningSegment = useCallback(
    (segment: string) => {
      setResultsTabStore('walls', 'stiffening')
      select({ type: 'stiffening', guids: [segment] }, { adopt: 'keep' })
    },
    [setResultsTabStore, select],
  )

  /** Select a tensile anchor (support guid) and open the walls "Anker" sub-tab. */
  const selectResultsAnchor = useCallback(
    (supportGuid: string) => {
      setResultsTabStore('walls', 'anchors')
      select({ type: 'anchor', guids: [supportGuid] }, { adopt: 'keep' })
      setResultsIsMultiEditMode(false)
    },
    [setResultsTabStore, select, setResultsIsMultiEditMode],
  )

  /** Select one or many tensile anchors in the walls "Anker" sub-tab. */
  const setResultsAnchorSelection = useCallback(
    (supportGuids: string[]) => {
      setResultsTabStore('walls', 'anchors')
      select(supportGuids.length ? { type: 'anchor', guids: supportGuids } : null, {
        adopt: 'keep',
      })
      setResultsIsMultiEditMode(supportGuids.length > 1)
    },
    [setResultsTabStore, select, setResultsIsMultiEditMode],
  )

  const selectResultsShearAnchor = useCallback(
    (segmentGuid: string, position: ShearAnchorPosition = 'bottom') => {
      setResultsTabStore('walls', 'shear-anchors')
      select(
        { type: 'shear-anchor', locations: [{ guid: segmentGuid, position }] },
        { adopt: 'keep' },
      )
      setResultsIsMultiEditMode(false)
    },
    [setResultsTabStore, select, setResultsIsMultiEditMode],
  )

  /** Select one or many Schubanker in the walls "Schubanker" sub-tab. */
  const setResultsShearAnchorSelection = useCallback(
    (locations: ShearAnchorLocationSelection[]) => {
      setResultsTabStore('walls', 'shear-anchors')
      select(locations.length ? { type: 'shear-anchor', locations } : null, { adopt: 'keep' })
      setResultsIsMultiEditMode(locations.length > 1)
    },
    [setResultsTabStore, select, setResultsIsMultiEditMode],
  )

  const setResultsSelection = useCallback(
    (ids: string[]) => {
      select(toResultsPlanarSelection(ids), { adopt: 'keep' })
      setResultsTracedLoads(null)
      setResultsIsMultiEditMode(ids.length > 1)
    },
    [select, toResultsPlanarSelection, setResultsTracedLoads, setResultsIsMultiEditMode],
  )

  const addResultsSelection = useCallback(
    (ids: string[]) => {
      const currentSelection = useSelectionStore.getState().selection
      const currentGuids =
        currentSelection?.type === 'planar' ? currentSelection.guids : emptyElements
      const newGuids = uniq([...currentGuids, ...ids])
      select(toResultsPlanarSelection(newGuids), { adopt: 'keep' })
      setResultsIsMultiEditMode(newGuids.length > 1)
    },
    [select, toResultsPlanarSelection, setResultsIsMultiEditMode],
  )

  const removeResultsSelection = useCallback(
    (id: string) => {
      const currentSelection = useSelectionStore.getState().selection
      const currentGuids =
        currentSelection?.type === 'planar' ? currentSelection.guids : emptyElements
      const newGuids = currentGuids.filter(selectedId => selectedId !== id)
      select(toResultsPlanarSelection(newGuids), { adopt: 'keep' })
      setResultsIsMultiEditMode(newGuids.length > 1)
    },
    [select, toResultsPlanarSelection, setResultsIsMultiEditMode],
  )

  const resetResultsSelection = useCallback(() => {
    clearSelection()
    setResultsDetailTab('design')
    setResultsTracedLoads(null)
    setResultsIsMultiEditMode(false)
  }, [clearSelection, setResultsDetailTab, setResultsTracedLoads, setResultsIsMultiEditMode])

  const selectResultsLoad = useCallback(
    (load: string | null) => {
      selectSub(
        load ? { type: 'load', guid: load, elementGuid: selectedElements[0] ?? null } : null,
      )
      setResultsTracedLoads(null)
      setResultsLocalTab('loads')
    },
    [selectedElements, selectSub, setResultsTracedLoads, setResultsLocalTab],
  )

  const selectResultsConnector = useCallback(
    (connector?: string) => {
      selectSub(
        connector
          ? { type: 'connector', guid: connector, elementGuid: selectedElements[0] ?? null }
          : null,
      )
    },
    [selectedElements, selectSub],
  )

  const setResultsVerticalTransmitter = useCallback(
    (transmitter: string | null) => {
      selectSub(
        transmitter
          ? {
              type: 'vertical-transmitter',
              guid: transmitter,
              elementGuid: selectedElements[0] ?? null,
            }
          : null,
      )
    },
    [selectedElements, selectSub],
  )

  const setResultsSelectedCheckPosition = useCallback(
    (position: string | null) => {
      selectSub(
        position
          ? {
              type: 'member-position',
              guid: position,
              elementGuid: selectedElements[0] ?? null,
            }
          : null,
      )
    },
    [selectedElements, selectSub],
  )

  const toggleResultsMode = useCallback(
    (newMode?: ResultsViewMode) => {
      switchMode(!newMode || activeMode === newMode ? 'neutral' : newMode)
    },
    [activeMode, switchMode],
  )

  const setResultsMode = useCallback(
    (newMode: ResultsViewMode | null) => {
      switchMode(newMode ?? 'neutral')
    },
    [switchMode],
  )

  return {
    selectedElements,
    selectedStiffeningSegment,
    selectedAnchor,
    selectedAnchors,
    selectedShearAnchor,
    selectedShearAnchorPosition,
    selectedShearAnchorLocations,
    selectedLoad,
    selectedConnector,
    selectedVerticalTransmitter,
    selectedCheckPosition,
    activeMode,
    selectResultsPosition,
    selectResultsSurface,
    selectResultsStiffeningSegment,
    selectResultsAnchor,
    setResultsAnchorSelection,
    selectResultsShearAnchor,
    setResultsShearAnchorSelection,
    setResultsSelection,
    addResultsSelection,
    removeResultsSelection,
    resetResultsSelection,
    selectResultsLoad,
    selectResultsConnector,
    setResultsVerticalTransmitter,
    setResultsSelectedCheckPosition,
    toggleResultsMode,
    setResultsMode,
  }
}

export default useResultsSelection
